import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  Download,
  KeyRound,
  Loader2,
  Search,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useManagerCatalog,
  useManagerSyncRun,
  useManagerSyncStatus,
  useSetBiManagerKey,
} from "@/hooks/useManagerSync";
import {
  isResolvableMapping,
  type ManagerCatalogEntry,
  type ManagerSyncStatus,
} from "@/api/managerSync";

/**
 * D-048 component D — статус manager-scoped BI-синхронізації.
 *   ManagerSyncSection      → drawer #1 (mapping-статус MP2 + last-run MP6 +
 *                             biManagerKey catalog-picker (Phase 2) + retry,
 *                             allowedActions-gated D-017)
 *   ManagerSyncWizardPanel  → wizard-фінал (читає CManagerScopedSyncRun, MP3)
 *
 * Phase 2: «Зіставлено по GUID» (resolvable_key) — ключ обирається з BI-каталогу
 * (name-assisted picker: name · code_1c · email · агент); null/shared-email
 * менеджери більше не блок. Каталог сервиться з кешу (runner оновлює).
 */

const RUN_LABEL: Record<string, string> = {
  queued: "у черзі",
  running: "виконується…",
  success: "успішно",
  empty: "клієнтів немає",
  error: "помилка",
};

function MappingBadge({ status }: { status: ManagerSyncStatus | undefined }) {
  if (!status) return null;
  const m = status.mapping;
  if (m.status === "resolvable_key") {
    return (
      <span className="inline-flex items-center gap-1 rounded-badge bg-success px-2 py-0.5 text-xs text-success-foreground">
        <KeyRound className="h-3.5 w-3.5" /> Зіставлено по GUID
        {m.biManagerKeySource === "sync" ? " (авто)" : ""}
      </span>
    );
  }
  if (m.status === "resolvable_email") {
    return (
      <span className="inline-flex items-center gap-1 rounded-badge bg-success px-2 py-0.5 text-xs text-success-foreground">
        <CheckCircle2 className="h-3.5 w-3.5" /> Зіставлення по email
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-xs text-warning-foreground">
      <AlertTriangle className="h-3.5 w-3.5" />
      {m.status === "ambiguous_email"
        ? "Неоднозначний email — оберіть з каталогу"
        : "Email відсутній — оберіть з каталогу"}
    </span>
  );
}

function RunBadge({ state }: { state: string }) {
  const variant =
    state === "success" ? "success" : state === "error" ? "destructive" : "muted";
  return <Badge variant={variant}>{RUN_LABEL[state] ?? state}</Badge>;
}

/** Phase 2 — catalog-picker (name-assisted): name · code_1c · email · агент. */
function CatalogPicker({
  currentKey,
  pending,
  onPick,
  onClear,
  onManual,
  onCancel,
}: {
  currentKey: string | null;
  pending: boolean;
  onPick: (entry: ManagerCatalogEntry) => void;
  onClear: () => void;
  onManual: () => void;
  onCancel: () => void;
}) {
  const catalogQ = useManagerCatalog(true);
  const [filter, setFilter] = useState("");

  const items = useMemo(() => {
    const all = catalogQ.data?.items ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter((it) =>
      [it.name, it.code1c, it.email, it.managerIdHex]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q)),
    );
  }, [catalogQ.data, filter]);

  return (
    <div className="mt-2 rounded-lg border bg-card p-2">
      <div className="relative mb-1.5">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Пошук: імʼя, код 1С, email…"
          className="h-7 pl-7 text-xs"
        />
      </div>

      {catalogQ.isLoading ? (
        <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Каталог завантажується…
        </div>
      ) : !catalogQ.data?.available ? (
        <p className="px-1 py-2 text-xs text-muted-foreground">
          Каталог BI ще не закешовано — sync-runner оновить його протягом години.
          Поки що можна вказати GUID вручну.
        </p>
      ) : (
        <ul className="max-h-48 overflow-y-auto" role="listbox" aria-label="BI-каталог менеджерів">
          {items.length === 0 && (
            <li className="px-1 py-2 text-xs text-muted-foreground">Нічого не знайдено.</li>
          )}
          {items.map((it) => {
            const selected = currentKey !== null && it.managerIdHex === currentKey;
            return (
              <li key={it.managerIdHex}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={pending}
                  onClick={() => onPick(it)}
                  className={`w-full rounded px-1.5 py-1 text-left text-xs hover:bg-accent disabled:opacity-50 ${
                    selected ? "bg-accent" : ""
                  } ${!it.isActive ? "opacity-60" : ""}`}
                >
                  <span className="font-medium">{it.name ?? it.managerIdHex}</span>
                  {it.isAgent ? (
                    <Badge variant="muted" className="ml-1.5 px-1 py-0 text-[10px]">
                      агент
                    </Badge>
                  ) : null}
                  {!it.isActive ? (
                    <Badge variant="muted" className="ml-1 px-1 py-0 text-[10px]">
                      неактивний
                    </Badge>
                  ) : null}
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {it.code1c ?? "—"} · {it.email ?? "без email"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-1.5 flex items-center justify-between border-t pt-1.5">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-[11px]"
            onClick={onManual}
          >
            Вказати вручну
          </Button>
          {currentKey !== null && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px] text-destructive"
              disabled={pending}
              onClick={onClear}
            >
              Очистити ключ
            </Button>
          )}
        </div>
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px]" onClick={onCancel}>
          Скасувати
        </Button>
      </div>
      {catalogQ.data?.stale && catalogQ.data.available && (
        <p className="mt-1 px-1 text-[10px] text-muted-foreground">
          Кеш каталогу старший за 24 год (оновлено {catalogQ.data.fetchedAt ?? "—"}).
        </p>
      )}
    </div>
  );
}

/** Drawer #1 секція — лише для tier=manager. */
export function ManagerSyncSection({
  userId,
  onToast,
}: {
  userId: string;
  onToast: (kind: "ok" | "err", msg: string) => void;
}) {
  const statusQ = useManagerSyncStatus(userId);
  const runM = useManagerSyncRun(userId);
  const keyM = useSetBiManagerKey(userId);
  /** "closed" | "picker" | "manual" — стан вибору biManagerKey. */
  const [keyEditor, setKeyEditor] = useState<"closed" | "picker" | "manual">("closed");
  const [manualKey, setManualKey] = useState("");

  const d = statusQ.data;
  if (statusQ.isLoading) {
    return (
      <div className="mt-5 flex items-center gap-2 border-t pt-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> BI-синхронізація…
      </div>
    );
  }
  if (!d) return null;

  const lr = d.lastRun;
  const canRun = d.allowedActions.canRun;
  const currentKey = d.mapping.biManagerKey;

  const saveKey = (key: string | null, okMsg: string) =>
    keyM.mutate(key, {
      onSuccess: () => {
        setKeyEditor("closed");
        setManualKey("");
        onToast("ok", okMsg);
      },
      onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
    });

  return (
    <div className="mt-5 border-t pt-4">
      <div className="mb-2 text-sm font-medium">BI-синхронізація</div>
      <dl className="text-sm">
        <div className="flex items-start justify-between gap-3 py-1.5">
          <dt className="shrink-0 text-xs text-muted-foreground">Зіставлення</dt>
          <dd className="text-right">
            <MappingBadge status={d} />
            {d.mapping.reason && !isResolvableMapping(d.mapping.status) && (
              <p className="mt-1 max-w-[220px] text-right text-[11px] leading-snug text-muted-foreground">
                {d.mapping.reason}
              </p>
            )}
          </dd>
        </div>

        <div className="flex items-start justify-between gap-3 py-1.5">
          <dt className="shrink-0 pt-0.5 text-xs text-muted-foreground">biManagerKey</dt>
          <dd className="min-w-0 grow text-right">
            {keyEditor === "closed" && (
              <span className="inline-flex items-center gap-1.5">
                <span className="max-w-[170px] truncate text-xs" title={currentKey ?? undefined}>
                  {currentKey ?? "—"}
                  {d.mapping.biManagerKeySource ? ` (${d.mapping.biManagerKeySource})` : ""}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-[11px]"
                  onClick={() => setKeyEditor("picker")}
                >
                  {currentKey ? "Змінити" : "Обрати з каталогу"}
                </Button>
              </span>
            )}
          </dd>
        </div>
      </dl>

      {keyEditor === "picker" && (
        <CatalogPicker
          currentKey={currentKey}
          pending={keyM.isPending}
          onPick={(entry) =>
            saveKey(entry.managerIdHex, `Зіставлено: ${entry.name ?? entry.managerIdHex}`)
          }
          onClear={() => saveKey(null, "biManagerKey очищено")}
          onManual={() => {
            setManualKey(currentKey ?? "");
            setKeyEditor("manual");
          }}
          onCancel={() => setKeyEditor("closed")}
        />
      )}

      {keyEditor === "manual" && (
        <div className="mt-2 flex items-center gap-1.5">
          <Input
            autoFocus
            value={manualKey}
            onChange={(e) => setManualKey(e.target.value)}
            placeholder="manager_id_hex (32 hex-символи)"
            className="h-7 grow text-xs"
          />
          <Button
            size="sm"
            className="h-7 px-2 text-[11px]"
            disabled={keyM.isPending}
            onClick={() =>
              saveKey(
                manualKey.trim() === "" ? null : manualKey.trim().toLowerCase(),
                "biManagerKey збережено",
              )
            }
          >
            {keyM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "OK"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-1.5 text-[11px]"
            onClick={() => setKeyEditor("picker")}
          >
            Назад
          </Button>
        </div>
      )}

      <dl className="text-sm">
        <div className="flex items-start justify-between gap-3 py-1.5">
          <dt className="shrink-0 text-xs text-muted-foreground">Останній прогін</dt>
          <dd className="text-right">
            {lr ? (
              <>
                <RunBadge state={lr.status} />
                {(lr.status === "success" || lr.status === "empty") && (
                  // MP6: BI-found · CRM-upserted · visible-after-ACL (+ шлях)
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    BI: {lr.foundCount} · CRM: +{lr.createdCount}/~{lr.updatedCount} · Видимо
                    після ACL: {d.visibleAfterAclCount} ·{" "}
                    {lr.managerKeyType === "guid" ? "по GUID" : "по email"}
                  </p>
                )}
                {lr.status === "error" && lr.errorMessage && (
                  <p className="mt-1 max-w-[220px] text-right text-[11px] leading-snug text-destructive">
                    {lr.errorMessage}
                  </p>
                )}
              </>
            ) : (
              <span className="text-xs text-muted-foreground">ще не запускався</span>
            )}
          </dd>
        </div>
      </dl>

      <div className="mt-2.5">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={!canRun || runM.isPending}
          onClick={() =>
            runM.mutate(undefined, {
              onSuccess: () => onToast("ok", "Підтягування клієнтів запущено"),
              onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
            })
          }
        >
          {runM.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-1.5 h-4 w-4" />
          )}
          Підтягнути клієнтів
        </Button>
        {!canRun && d.allowedActions.reason && (
          <p className="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-muted-foreground">
            <CircleSlash className="mt-0.5 h-3 w-3 shrink-0" /> {d.allowedActions.reason}
          </p>
        )}
      </div>
    </div>
  );
}

/** Wizard-фінал: post-create панель (MP3 — статус з CManagerScopedSyncRun). */
export function ManagerSyncWizardPanel({ userId }: { userId: string }) {
  const statusQ = useManagerSyncStatus(userId);
  const d = statusQ.data;

  if (statusQ.isLoading || !d) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2.5 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Перевірка BI-синхронізації…
      </div>
    );
  }

  if (!d.flagEnabled) {
    return (
      <div className="rounded-lg bg-muted px-3 py-2.5 text-sm text-muted-foreground">
        BI-синхронізацію вимкнено (d048_manager_sync).
      </div>
    );
  }

  if (!isResolvableMapping(d.mapping.status)) {
    return (
      <div className="rounded-lg bg-warning px-3 py-2.5 text-sm text-warning-foreground">
        <span className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-4 w-4" /> Підтягування клієнтів не запущено
        </span>
        <p className="mt-1 text-xs">{d.mapping.reason}</p>
        <p className="mt-1 text-xs">
          Зіставте менеджера з BI-каталогом у картці користувача (biManagerKey) і
          натисніть «Підтягнути клієнтів».
        </p>
      </div>
    );
  }

  const lr = d.lastRun;
  if (!lr || lr.status === "queued" || lr.status === "running") {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-info px-3 py-2.5 text-sm text-info-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Запущено підтягування клієнтів із BI · виконується…
      </div>
    );
  }
  if (lr.status === "success") {
    return (
      <div className="rounded-lg bg-success px-3 py-2.5 text-sm text-success-foreground">
        <span className="flex items-center gap-1.5 font-medium">
          <CheckCircle2 className="h-4 w-4" /> Клієнтів підтягнуто: {lr.foundCount}
        </span>
        <p className="mt-1 text-xs">
          Створено {lr.createdCount} · оновлено {lr.updatedCount} · видимо після ACL:{" "}
          {d.visibleAfterAclCount}
        </p>
      </div>
    );
  }
  if (lr.status === "empty") {
    return (
      <div className="rounded-lg bg-secondary px-3 py-2.5 text-sm">
        Клієнтів у BI ще немає. Перевірте привʼязку в 1С і натисніть «Підтягнути клієнтів»
        пізніше (drawer користувача).
      </div>
    );
  }
  return (
    <div className="rounded-lg bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
      <span className="flex items-center gap-1.5 font-medium">
        <XCircle className="h-4 w-4" /> Помилка підтягування
      </span>
      {lr.errorMessage && <p className="mt-1 text-xs">{lr.errorMessage}</p>}
    </div>
  );
}
